import React, { memo, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBreakpoints, useMount } from '@mafc/hooks';
import {
  IAppState,
  useAnalytic,
  CookieUtils,
  setPageIntent,
  useDeliveryHours,
  HeadTabActions,
  setSelectedTabId,
  setPageName,
  useNonFoodV2Journey,
} from '@mafc/common';
import useReferrerUri from 'pages/Checkout/hooks/useReferrerUri.hook';
import CartCheckoutContainer from 'common/CartCheckoutContainer';
import { userSetAddressChange } from 'common/User/redux/User.actions';
import { hideNotification } from 'common/Notification/redux/notification.action';
import { hidePageLoader } from 'common/PageLoader/redux/pageLoader.action';
import DynamicLoader from 'routes/DynamicLoader';

import Tracker from 'utils/Tracker';
import { initStorageManager } from 'utils/customStorage.util';
import { scrollToTop } from 'helpers/window.helpers';
import { BrazeUserAttributes } from 'pages/OrderConfirmation/utils/brazeContants';

/** Old Cart imports */
import useMergeCarts from '../Cart/hooks/useMergeCarts.hook';
import CartLoader from '../Cart/components/CartLoader';
import { COUNTRY_CODE_KEY } from '../Cart/constants/cart.constants';

/** New Cart imports */
import useCartShipments from './hooks/useCartShipments';
import useGTMVirtualPageTrakingEffect from './hooks/useGTMVirtualPageTrakingEffect';
import useProductIds from './hooks/useProductIds';
import useRelevantCarousel from './hooks/useRelevantCarousel';
import BreadCrumb from './components/Breadcrumb';
import RRCarousal from './components/RRCarousel';
import ShipmentList from './components/ShipmentList';
import { VARIANT } from './constants/carousel.constants';
import {
  fetchCart,
  viewBasketTracking,
  fetchAdtechCarousel,
} from './redux/actions/fetchCart.actions';

import * as S from './Cart.style';
import NowOffLineHours from './components/NowOffLineHours/index';
import PaymentMethod from 'common/PaymentMethods';
import { Box } from '@mafc/trolly';
import { useFeature } from '@mafc/appshell';
import { GROWTHBOOK_FLAGS } from './constants/growthbook.constants';
import OOSMessage from './components/OOSMessage';
import { EShipmentTypes } from 'services/shipments/types/shipments.types';
import { INewCartEntry, INewCartShipment } from './ICart';
import useOOSSubstitute from 'hooks/useOOSSubstitute';
import OOSSubstitutesError from './components/OOSSubstitutesError';
import Cookies from 'universal-cookie';
import AdtechCartCarousel from './components/AdtechCartCarousel';
import { getCartPageUrl } from 'utils/cartRevamp.util';
import useCartRevampEnabled from 'hooks/useCartRevampEnabled';

const storage = initStorageManager(() => sessionStorage);

const EmptyCartView = DynamicLoader(() => import('../Cart/components/EmptyCartView')) as any;
const CartOrderSummary = DynamicLoader(() =>
  import('../Cart/components/CartOrderSummary/index')
) as any;
const EnableSubstitution = DynamicLoader(() =>
  import(
    /* webpackChunkName: "groceryChunk" */
    '../Cart/components/EnableSubstitution'
  )
) as any;

const Cart: React.FC = () => {
  const isCartRevampEnabled = useCartRevampEnabled();
  const { code: countryCode, storeId, language } = useSelector(
    (state: IAppState) => state.appConfig
  );
  const NEW_CART_PAGE = getCartPageUrl(storeId, language, isCartRevampEnabled);
  useReferrerUri(NEW_CART_PAGE);
  useGTMVirtualPageTrakingEffect('cart');

  const { shipments, isLoading, isCartEmpty, refreshSubstitutes } = useCartShipments();
  const { isMobile } = useBreakpoints();
  const dispatch = useDispatch();
  // multi-lingual support
  const { t } = useTranslation();

  const { type, addressChanged, selectedAddress, errorFetchSubstitutes } = useSelector(
    (state: IAppState) => {
      return { ...state.notification, ...state.userData, ...state.newCart };
    }
  );

  const { BrazeSetCustomUserAttribute } = useAnalytic();
  const adTechCriteoEnabled = useFeature(GROWTHBOOK_FLAGS.CRITEO_AD_ENABLED).on;
  const oosReplacementEnabled = useOOSSubstitute();
  // Merge Carts
  useMergeCarts(t);

  useEffect(() => {
    storage.setItem(COUNTRY_CODE_KEY, countryCode);
  }, [countryCode]);

  useMount(() => {
    Tracker.gtmPageLoad('cart_page');
    CookieUtils.setPageType('cart');
    dispatch(setPageName('cart'));
    dispatch(setPageIntent(true));
    dispatch(hidePageLoader());
  });

  useEffect(() => {
    if (type !== 'error') {
      hideNotification();
    }
  }, [type]);

  useEffect(() => {
    window.document.title = t('header.cart');
  }, [t]);

  const store = (window as any).__REDUX_STORE__;
  const callStore = () => {
    return store ? store.getState() : {};
  };
  const isNonFoodV2Enabled = useNonFoodV2Journey();
  const selectedTab = CookieUtils.getSelectedTabData() || {};
  const isAdtechCarouselEnabled = useFeature(GROWTHBOOK_FLAGS.ADTECH_CAROUSEL_ENABLED).on;
  useEffect(() => {
    if (store && isNonFoodV2Enabled) {
      HeadTabActions.userJourneyActions(dispatch, callStore, new Cookies());
      dispatch(setSelectedTabId(selectedTab.selectedTabId || -1));
    }
    if (isAdtechCarouselEnabled) {
      dispatch(fetchAdtechCarousel(selectedTab.serviceType));
    }
  }, []);

  useEffect(() => {
    const brazeLastViewedPage = `${storeId}/${language}${window.location.pathname}`;
    BrazeSetCustomUserAttribute({ [BrazeUserAttributes.last_viewed_page]: brazeLastViewedPage });
  }, [BrazeSetCustomUserAttribute, language, storeId]);

  useEffect(() => {
    if (addressChanged) {
      dispatch(fetchCart());
      dispatch(userSetAddressChange(false));
    }
    if (adTechCriteoEnabled && shipments && shipments.length) {
      dispatch(viewBasketTracking());
    }
  }, [dispatch, addressChanged, selectedAddress, shipments, adTechCriteoEnabled]);

  useLayoutEffect(() => {
    scrollToTop();
  }, [isCartEmpty]);

  const {
    isNowUnavailableHour,
    isNowOfflineHour,
    nextAvailableSlotForNow = {},
    nowBusyHourDisclaimer,
  } = useDeliveryHours();
  const productIds = useProductIds(shipments);

  const carousels = useRelevantCarousel(productIds);

  const replaceableOOSEntries = shipments.reduce(
    (accumulator: INewCartEntry[], shipment: INewCartShipment) => {
      if (
        (shipment.shipmentType === EShipmentTypes.EXPRESS && !isNowUnavailableHour) ||
        shipment.shipmentType === EShipmentTypes.STANDARD_FOOD
      ) {
        return [...accumulator, ...shipment.oosEntries];
      }
      return accumulator;
    },
    []
  );

  return (
    <>
      {isMobile && isNowUnavailableHour && (
        <NowOffLineHours
          busyHourDisclaimer={nowBusyHourDisclaimer}
          isNowOfflineHour={isNowOfflineHour}
          nextAvailableSlot={nextAvailableSlotForNow}
          isMobile
        />
      )}
      <BreadCrumb />

      {isCartEmpty ? (
        <EmptyCartView />
      ) : (
        // @ts-ignore
        <CartCheckoutContainer>
          <CartCheckoutContainer.Flex
            {...{
              px: [3, null, null, null, 0],
            }}
          >
            {isLoading ? (
              <CartLoader />
            ) : (
              <S.LeftSide>
                {!isMobile && isNowUnavailableHour && (
                  <NowOffLineHours
                    busyHourDisclaimer={nowBusyHourDisclaimer}
                    isNowOfflineHour={isNowOfflineHour}
                    nextAvailableSlot={nextAvailableSlotForNow}
                  />
                )}
                <RRCarousal variant={VARIANT.TOP} productIds={productIds} carousels={carousels} />

                <EnableSubstitution />

                {oosReplacementEnabled && replaceableOOSEntries.length > 0 && (
                  <OOSMessage count={replaceableOOSEntries.length} />
                )}

                {errorFetchSubstitutes && (
                  <OOSSubstitutesError
                    count={replaceableOOSEntries.length}
                    onRefresh={refreshSubstitutes}
                  />
                )}

                <ShipmentList
                  shipments={shipments}
                  isNowOfflineHour={isNowOfflineHour}
                  isNowUnavailableHour={isNowUnavailableHour}
                  nextAvailableSlot={nextAvailableSlotForNow}
                  busyHourDisclaimer={nowBusyHourDisclaimer}
                />
                <Box display={['block', 'block', 'block', 'none']} mb={3}>
                  <PaymentMethod isCart />
                </Box>
                {/* <RRCarousal
                  variant={VARIANT.BOTTOM}
                  productIds={productIds}
                  carousels={carousels}
                /> */}
                {isAdtechCarouselEnabled && <AdtechCartCarousel />}
              </S.LeftSide>
            )}
            <CartOrderSummary isLoading={isLoading} />
          </CartCheckoutContainer.Flex>
        </CartCheckoutContainer>
      )}
    </>
  );
};

export default memo(Cart);
